/**
 * Material UI Theme Configuration
 *
 * This file defines the base/light theme for the Escuela Preparatoria 5 portal.
 * It includes custom colors, typography, component defaults, and spacing.
 *
 * Future: Can be extended to support dark mode.
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import { typographyVariants } from './typography';

// Define custom color palette
const colors = {
	primary: {
		main: '#4355ab', // Main blue color
		light: '#6b7ec8',
		dark: '#2e3b75',
		contrastText: '#ffffff',
	},
	secondary: {
		main: '#f5f5f5',
		light: '#ffffff',
		dark: '#e0e0e0',
		contrastText: '#333333',
	},
	background: {
		default: '#f5f5f5',
		paper: '#ffffff',
		drawer: '#f8f9fa',
	},
	text: {
		primary: '#333333',
		secondary: '#666666',
		disabled: '#999999',
	},
	divider: '#e0e0e0',
	action: {
		hover: '#e8eaf6',
		selected: '#4355ab',
	},
};

// Theme configuration
const themeOptions: ThemeOptions = {
	palette: {
		mode: 'light',
		primary: colors.primary,
		secondary: colors.secondary,
		background: {
			default: colors.background.default,
			paper: colors.background.paper,
		},
		text: colors.text,
		divider: colors.divider,
	},
	typography: typographyVariants,
	spacing: 8, // Base spacing unit (8px)
	shape: {
		borderRadius: 8,
	},
	components: {
		// MuiDrawer customization
		MuiDrawer: {
			styleOverrides: {
				paper: {
					backgroundColor: colors.background.drawer,
					borderRight: `1px solid ${colors.divider}`,
				},
			},
		},
		// MuiListItemButton customization for navigation
		MuiListItemButton: {
			styleOverrides: {
				root: {
					borderRadius: 8,
					marginBottom: 4,
					'&.Mui-selected': {
						backgroundColor: colors.primary.main,
						color: colors.primary.contrastText,
						'&:hover': {
							backgroundColor: colors.primary.main,
						},
						'& .MuiListItemIcon-root': {
							color: colors.primary.contrastText,
						},
					},
					'&:hover': {
						backgroundColor: colors.action.hover,
					},
				},
			},
		},
		// MuiListItemIcon customization
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					minWidth: 40,
					color: colors.text.secondary,
				},
			},
		},
		// MuiListItemText customization
		MuiListItemText: {
			styleOverrides: {
				primary: {
					fontSize: '0.9rem',
				},
			},
		},
		// MuiButton customization
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
					fontWeight: 500,
					borderRadius: 8,
				},
				contained: {
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none',
					},
				},
			},
		},
		// MuiCard customization
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 12,
					boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
				},
			},
		},
		// MuiPaper customization
		MuiPaper: {
			styleOverrides: {
				root: {
					borderRadius: 12,
				},
				elevation1: {
					boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
				},
			},
		},
		// MuiAppBar customization
		MuiAppBar: {
			styleOverrides: {
				root: {
					boxShadow: 'none',
					borderBottom: `1px solid ${colors.divider}`,
				},
			},
		},
	},
};

// Create and export the theme
export const lightTheme = createTheme(themeOptions);

// Export colors for use in components if needed
export { colors };
